import Papa from 'papaparse';
import type { SalesImportRow } from './api';

const HEADER_MAP: Record<string, keyof SalesImportRow> = {
  // monitor_id
  sku: 'monitor_id',
  monitor_id: 'monitor_id',
  'sku / mã sp': 'monitor_id',
  'mã sp': 'monitor_id',
  'mã sản phẩm': 'monitor_id',
  id: 'monitor_id',
  // month
  month: 'month',
  tháng: 'month',
  // units_sold
  units_sold: 'units_sold',
  units: 'units_sold',
  'sản lượng': 'units_sold',
  'units bán': 'units_sold',
  'units sold': 'units_sold',
  // revenue
  revenue_vnd: 'revenue_vnd',
  revenue: 'revenue_vnd',
  'doanh thu': 'revenue_vnd',
  // margin
  margin_vnd: 'margin_vnd',
  margin: 'margin_vnd',
  'lợi nhuận': 'margin_vnd',
  'ln vnd': 'margin_vnd',
  // stock
  stock_qty: 'stock_qty',
  stock: 'stock_qty',
  'tồn kho': 'stock_qty',
  inventory: 'stock_qty',
};

// ---- Value parsers ----

function parseVietnameseNumber(s: string | number | undefined | null): number {
  if (s === null || s === undefined || s === '') return 0;
  if (typeof s === 'number') return isNaN(s) ? 0 : s;
  const cleaned = String(s).replace(/\./g, '').replace(/[^\d,-]/g, '').replace(/,/g, '.').trim();
  if (!cleaned || cleaned === '-') return 0;
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
}

function parseInteger(s: string | number | undefined | null): number {
  return Math.round(parseVietnameseNumber(s));
}

/**
 * Normalize month input to YYYY-MM format.
 * Accepts: "2026-03", "2026/03", "T3/2026", "Tháng 3 2026", "03/2026", "3/2026"
 */
export function normalizeMonth(s: string | undefined | null): string | null {
  if (!s) return null;
  const str = String(s).trim();

  // Already canonical: YYYY-MM
  let m = str.match(/^(\d{4})-(\d{1,2})$/);
  if (m) return `${m[1]}-${m[2]!.padStart(2, '0')}`;

  // YYYY/MM
  m = str.match(/^(\d{4})\/(\d{1,2})$/);
  if (m) return `${m[1]}-${m[2]!.padStart(2, '0')}`;

  // T M/YYYY  (vd: T3/2026)
  m = str.match(/^T\s?(\d{1,2})\/(\d{4})$/i);
  if (m) return `${m[2]}-${m[1]!.padStart(2, '0')}`;

  // Tháng M YYYY  (vd: "Tháng 3 2026")
  m = str.match(/^Tháng\s+(\d{1,2})\s+(\d{4})$/i);
  if (m) return `${m[2]}-${m[1]!.padStart(2, '0')}`;

  // MM/YYYY
  m = str.match(/^(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[2]}-${m[1]!.padStart(2, '0')}`;

  return null;
}

// ---- Main parser ----

export interface SalesCsvParseResult {
  rows: SalesImportRow[];
  warnings: string[];
  errors: string[];
  unmappedHeaders: string[];
}

function buildHeaderMapping(fields: string[]): {
  mapping: Record<string, keyof SalesImportRow>;
  unmapped: string[];
} {
  const mapping: Record<string, keyof SalesImportRow> = {};
  const unmapped: string[] = [];
  for (const h of fields) {
    const norm = h.toLowerCase().trim();
    const target = HEADER_MAP[norm];
    if (target) mapping[h] = target;
    else unmapped.push(h);
  }
  return { mapping, unmapped };
}

function transformRow(
  raw: Record<string, string>,
  mapping: Record<string, keyof SalesImportRow>,
  warnings: string[],
  rowNum: number
): SalesImportRow | null {
  const out: Record<string, unknown> = {};

  for (const [csvHeader, fieldName] of Object.entries(mapping)) {
    const v = raw[csvHeader];
    if (v === undefined || v === '' || v === null) continue;

    switch (fieldName) {
      case 'monitor_id':
        out.monitor_id = String(v).trim();
        break;
      case 'month': {
        const norm = normalizeMonth(String(v));
        if (norm) out.month = norm;
        else warnings.push(`Dòng ${rowNum}: month "${v}" không nhận diện được`);
        break;
      }
      case 'units_sold':
      case 'stock_qty':
        out[fieldName] = parseInteger(v);
        break;
      case 'revenue_vnd':
      case 'margin_vnd':
        out[fieldName] = parseVietnameseNumber(v);
        break;
    }
  }

  if (!out.monitor_id) {
    warnings.push(`Dòng ${rowNum}: thiếu SKU/monitor_id, bỏ qua`);
    return null;
  }
  if (!out.month) {
    warnings.push(`Dòng ${rowNum} (${out.monitor_id}): thiếu hoặc sai format tháng, bỏ qua`);
    return null;
  }

  // Defaults
  if (out.units_sold == null) out.units_sold = 0;
  if (out.revenue_vnd == null) out.revenue_vnd = 0;
  if (out.margin_vnd == null) out.margin_vnd = 0;
  if (out.stock_qty == null) out.stock_qty = 0;

  return out as unknown as SalesImportRow;
}

export function parseSalesCSV(file: File): Promise<SalesCsvParseResult> {
  return new Promise((resolve) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (result) => {
        const warnings: string[] = [];
        const errors: string[] = [];
        const fields = result.meta.fields ?? [];
        const { mapping, unmapped } = buildHeaderMapping(fields);

        const mappedFields = new Set(Object.values(mapping));
        const missing: string[] = [];
        if (!mappedFields.has('monitor_id')) missing.push('SKU');
        if (!mappedFields.has('month')) missing.push('Tháng');

        if (missing.length > 0) {
          errors.push(
            `Thiếu cột bắt buộc: ${missing.join(', ')}. Header tìm thấy: ${fields.join(', ')}`
          );
          resolve({ rows: [], warnings, errors, unmappedHeaders: unmapped });
          return;
        }

        const rows: SalesImportRow[] = [];
        for (let i = 0; i < result.data.length; i++) {
          const row = transformRow(result.data[i]!, mapping, warnings, i + 2);
          if (row) rows.push(row);
        }

        // Detect duplicates (monitor_id, month)
        const seen = new Set<string>();
        const dupes = new Set<string>();
        for (const r of rows) {
          const key = `${r.monitor_id}|${r.month}`;
          if (seen.has(key)) dupes.add(key);
          seen.add(key);
        }
        if (dupes.size > 0) {
          warnings.push(
            `Có cặp (SKU, tháng) duplicate: ${Array.from(dupes).slice(0, 5).join(', ')}${
              dupes.size > 5 ? '...' : ''
            }`
          );
        }

        resolve({ rows, warnings, errors, unmappedHeaders: unmapped });
      },
      error: (err: Error) => {
        resolve({
          rows: [],
          warnings: [],
          errors: [`CSV parse failed: ${err.message}`],
          unmappedHeaders: [],
        });
      },
    });
  });
}
